/** Libellés et mise en forme rédactionnelle — ordonnance médicale. */

export function formatGenderFr(value) {
  if (value === 'M') return 'Homme';
  if (value === 'F') return 'Femme';
  if (value === 'OTHER') return 'Autre';
  return null;
}

export function formatPrescribedDateLong(value, fallbackFn) {
  if (!value) return fallbackFn ? fallbackFn() : '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return fallbackFn ? fallbackFn() : '—';
    return d.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  } catch {
    return fallbackFn ? fallbackFn() : '—';
  }
}

export function formatPrescribedDateTime(value, fallbackFn) {
  if (!value) return fallbackFn ? fallbackFn() : '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return fallbackFn ? fallbackFn() : '—';
    return d.toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' });
  } catch {
    return fallbackFn ? fallbackFn() : '—';
  }
}

export function formatQuantitySupply(qty, unitName) {
  if (qty == null || qty === '') return null;
  const n = Number(qty);
  if (!Number.isFinite(n)) return null;
  const unit = (unitName || '').trim();
  if (unit) {
    return n > 1 ? `${n} ${unit}` : `${n} ${unit.replace(/s$/, '')}`;
  }
  return n > 1 ? `${n} unités` : '1 unité';
}

/**
 * Lignes rédactionnelles pour une prescription médicamenteuse.
 */
export function buildMedicationRedaction(item) {
  const name = (item.medication_name || 'Médicament').trim();
  const qtyLine = formatQuantitySupply(item.quantity, item.unit_name);
  const poso = (item.dosage || '').trim();
  const duration = (item.duration || '').trim();
  const instructions = (item.instructions || '').trim();
  const itemNotes = (item.notes || '').trim();

  const detailLines = [];
  if (poso) detailLines.push(poso);
  if (duration && !poso.toLowerCase().includes(duration.toLowerCase())) {
    detailLines.push(`Durée : ${duration}`);
  }
  if (instructions) detailLines.push(instructions);
  if (itemNotes) detailLines.push(itemNotes);

  return { name, qtyLine, detailLines };
}

export function buildPatientIdentityBlock(consultation, prescription) {
  const name = (consultation?.patient_name || prescription?.patient_name || '').trim() || '—';
  const age =
    consultation?.patient_age != null && consultation?.patient_age !== ''
      ? `${consultation.patient_age} ans`
      : null;
  const gender = formatGenderFr(consultation?.patient_gender);
  const phone = (consultation?.patient_phone || prescription?.patient_phone || '').trim() || null;
  const matricule = (consultation?.member_matricule || '').trim() || null;
  const company =
    (consultation?.member_company_name || prescription?.member_company_name || '').trim() || null;

  return { name, age, gender, phone, matricule, company };
}

/** Sous-ligne patient : âge · sexe · matricule · société · téléphone. */
export function buildPatientDetailParts(patient) {
  if (!patient) return [];
  return [patient.age, patient.gender, patient.matricule, patient.company, patient.phone].filter(
    Boolean
  );
}

export function buildPatientDetailLine(patient) {
  const parts = buildPatientDetailParts(patient);
  return parts.length ? parts.join(' · ') : null;
}

/** Ligne compacte historique (nom + détails). */
export function buildPatientLine(patient) {
  if (!patient) return '—';
  const detail = buildPatientDetailLine(patient);
  if (!detail) return patient.name || '—';
  return `${patient.name || '—'} · ${detail}`;
}

export function buildPrescriptionMeta(consultation, prescription) {
  const prescribedDate =
    prescription?.prescribed_date ||
    consultation?.consultation_date ||
    prescription?.created_at ||
    consultation?.created_at ||
    null;

  return {
    consultationId: consultation?.id ?? prescription?.consultation_id ?? null,
    prescriptionId: prescription?.id ?? null,
    siteId: prescription?.site_id ?? consultation?.site_id ?? null,
    siteName: (prescription?.site_name || consultation?.site_name || '').trim() || null,
    doctorName: (prescription?.doctor_name || consultation?.doctor_name || '').trim() || null,
    prescribedDate,
    serviceLine: consultation?.service_line || prescription?.linked_service_line || null
  };
}

/** Référence documentaire discrète — ex. n° 0022 */
export function formatClinicalRefNumber(value, { pad = 4 } = {}) {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  const digits = Math.max(pad, String(Math.trunc(n)).length);
  return `n° ${String(Math.trunc(n)).padStart(digits, '0')}`;
}

export function formatCompactRefTag(prefix, value, { pad = 4 } = {}) {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  const digits = Math.max(pad, String(Math.trunc(n)).length);
  return `#${prefix}${String(Math.trunc(n)).padStart(digits, '0')}`;
}

/** Ligne de référence document — n° consultation, date (affichage discret). */
export function buildPrescriptionReferenceSegments(meta, { issueDate = null } = {}) {
  const segments = [];

  const consultationRef = formatClinicalRefNumber(meta?.consultationId);
  if (consultationRef) {
    segments.push({ key: 'consultation', text: consultationRef });
  }

  if (issueDate) {
    segments.push({ key: 'date', text: issueDate });
  }

  return segments;
}

/** Date + heure de prescription — ex. 15 juin 2026 à 15h27 */
export function formatPrescriptionIssueDateTime(value) {
  if (!value) return null;
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    const datePart = d.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    const timePart = d
      .toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', hour12: false })
      .replace(':', 'h');
    return `${datePart} à ${timePart}`;
  } catch {
    return null;
  }
}

/** Segments N° et date pour affichage structuré. */
export function buildPrescriptionRefParts(meta, { fallbackDate = null } = {}) {
  const consultationTag = formatCompactRefTag('C', meta?.consultationId);
  const prescriptionTag = formatCompactRefTag('O', meta?.prescriptionId);
  const prescriptionNumber = formatClinicalRefNumber(meta?.prescriptionId);
  const consultationNumber = formatClinicalRefNumber(meta?.consultationId);
  const date = formatPrescriptionIssueDateTime(meta?.prescribedDate || fallbackDate);
  if (!consultationTag && !prescriptionTag && !prescriptionNumber && !date) return null;
  return {
    consultationTag,
    prescriptionTag,
    prescriptionNumber,
    consultationNumber,
    date
  };
}

/** Ligne unique N° + date — ex. N° 0052 — 15 juin 2026 à 15h27 */
export function buildPrescriptionRefLine(meta) {
  const parts = buildPrescriptionRefParts(meta);
  if (!parts) return null;
  const segments = [];
  if (parts.consultationTag) segments.push(parts.consultationTag);
  if (parts.prescriptionTag) segments.push(parts.prescriptionTag);
  if (parts.date) segments.push(parts.date);
  return segments.join(' — ');
}

/** Index + nom médicament (sans quantité). */
export function formatMedicationIndexName(index, name) {
  return `${index + 1}. ${(name || 'Médicament').trim()}`;
}

/** Ligne médicament : nom + quantité sur la même ligne. */
export function formatMedicationTitleLine(index, name, qtyLine) {
  const label = formatMedicationIndexName(index, name);
  return qtyLine ? `${label} — ${qtyLine}` : label;
}

export function serviceLineLabel(line) {
  const map = {
    MEDICAL: 'Médecine générale',
    DENTAL: 'Filière dentaire',
    NURSING: 'Infirmerie'
  };
  return map[line] || line || null;
}
